import { ROUTER_PATH } from "@config/router";
import type {
  ReportGroup,
  ReportTaskItem,
} from "@features/report/reportDataAdapter";
import type { ContentSequenceShellItem } from "@screens/briefing/contentSequenceTypes";

export type ReportEmbedKind =
  "daily_summary" | "goal_details" | "tracker_details" | "report_end";

export type ReportSequenceItem = ContentSequenceShellItem & {
  embedKind: ReportEmbedKind;
};

const REPORT_END_ITEM: ReportSequenceItem = {
  recordId: "report-end",
  businessType: "REPORT_END",
  title: "That's everything for now",
  embedKind: "report_end",
  headerChrome: "opaque",
};

function buildMarketSummaryItem(
  market: NonNullable<ReportGroup["market_summary"]>,
): ReportSequenceItem {
  return {
    recordId: market.date,
    businessType: "DAILY_SUMMARY",
    title: market.title,
    embedKind: "daily_summary",
    headerChrome: "opaque",
  };
}

function buildGoalItem(task: ReportTaskItem): ReportSequenceItem {
  return {
    recordId: task.task_id,
    businessType: "GOAL",
    title: task.latest_unread_title,
    embedKind: "goal_details",
    headerChrome: "transparent",
  };
}

function buildTrackerItem(task: ReportTaskItem): ReportSequenceItem {
  return {
    recordId: task.task_id,
    businessType: "TRACKER_SIGNAL",
    title: task.latest_unread_title,
    embedKind: "tracker_details",
    headerChrome: "opaque",
  };
}

/**
 * 过滤脏 report_end 后保证末位恰一个（壳现逻辑回归锚点，AC27）。
 */
export function appendReportEndItem(
  items: ReportSequenceItem[],
): ReportSequenceItem[] {
  return [
    ...items.filter((item) => item.embedKind !== "report_end"),
    REPORT_END_ITEM,
  ];
}

/**
 * group → 序列 items（fe-spec §3.4.1：items = 汇报页 + report_end）。
 * 顺序：[market_summary?] + goals + trackers + [report_end]。
 * recordId 语义：market=market_summary.date、goal/tracker=task.task_id（`?start=` 定位键）。
 */
export function buildReportSequenceItems(
  group: ReportGroup,
): ReportSequenceItem[] {
  return appendReportEndItem([
    ...(group.market_summary
      ? [buildMarketSummaryItem(group.market_summary)]
      : []),
    ...group.goals.map(buildGoalItem),
    ...group.trackers.map(buildTrackerItem),
  ]);
}

export function buildBriefReportHref(args?: {
  start?: string;
  entry?: "report" | "auto";
  /** 跳过 F0 开场页，直进 slides（Opportunity Daily Market 等深链） */
  skipOpening?: boolean;
  /** mock 预览：通知开关 */
  notify?: "on" | "off";
  /** mock 预览：Brief 是否还有可读卡 */
  hasCards?: "0" | "1";
}): string {
  const params = new URLSearchParams();
  params.set("entry", args?.entry ?? "report");
  if (args?.skipOpening) {
    params.set("skipOpening", "1");
  }
  if (args?.start) {
    params.set("start", args.start);
  }
  if (args?.notify) {
    params.set("notify", args.notify);
  }
  if (args?.hasCards) {
    params.set("hasCards", args.hasCards);
  }
  return `${ROUTER_PATH.BRIEF_REPORT}?${params.toString()}`;
}
